// /book-a-demo: the booking page. Two steps: pick a time, then a short form.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { booking, founders, cta } from '../content';
import { usePageMeta, Swipe, SmartLink, Badge, cx } from '../ui';
import { ApiError, CONTACT_EMAIL, companyFromEmail, fmtDay, fmtTime, isEmail, isFreeMail, tzCity, useBooking, useBookingForm, type BookingForm } from './useBooking';
import { Alert, AutoHeight, Confirmation, DateGrid, Honeypot, IconArrow, IconCheck, NoSlots, PickerSkeleton, Progress, Spinner, TextField, TimeGrid, TzLine } from './parts';

const STEPS = ['Pick a time', 'Your details'];
type Errors = Partial<Record<keyof BookingForm | 'start', string>>;
const DETAIL_FIELDS = ['email', 'firstName', 'lastName', 'company', 'product', 'message'];

function validateDetails(f: BookingForm): Errors {
  const e: Errors = {};
  if (!f.email.trim()) e.email = 'Enter your email so we can send the invite';
  else if (!isEmail(f.email)) e.email = 'That email does not look right. Check for typos.';
  if (!f.firstName.trim()) e.firstName = 'Enter your first name';
  if (!f.lastName.trim()) e.lastName = 'Enter your last name';
  if (!f.company.trim()) e.company = 'Enter your company';
  return e;
}

// Reads ?email= once, then drops it from the address bar so it is not shared or logged.
function useEmailParam() {
  return useMemo(() => {
    if (typeof window === 'undefined') return null;
    const q = new URLSearchParams(window.location.search);
    const email = q.get('email');
    if (email) {
      q.delete('email');
      const qs = q.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
    }
    return email;
  }, []);
}

const finePointer = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: fine)').matches;

const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="mt-px shrink-0">
    <circle cx="8" cy="8" r="6.6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 7.2v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="8" cy="4.9" r="1" fill="currentColor" />
  </svg>
);

const scrollCardIntoView = () => {
  const card = document.getElementById('booking-card');
  if (card && card.getBoundingClientRect().top < 64) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export function BookingCard({ source, onBooked }: { source?: string; onBooked?: (booked: boolean) => void }) {
  const prefill = useEmailParam();
  const { form, set: setField } = useBookingForm(prefill);
  const bk = useBooking({ source });
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Errors>({});
  const set = <K extends keyof BookingForm>(k: K, v: BookingForm[K]) => {
    setField(k, v);
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };
  const [banner, setBanner] = useState<React.ReactNode>(null);
  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const headRef = useRef<HTMLHeadingElement>(null);
  const moved = useRef(false);

  const booked = !!bk.result && !bk.cancelled;
  useEffect(() => onBooked?.(booked), [booked, onBooked]);

  const dates = bk.availability?.dates ?? [];
  const day = dates.find((d) => d.date === date);

  // keep the chosen date valid as availability arrives or the time zone changes
  useEffect(() => {
    if (!bk.availability) return;
    const ds = bk.availability.dates;
    const withSlot = slot ? ds.find((d) => d.slots.some((s) => s.start === slot)) : undefined;
    if (slot && !withSlot) setSlot(null);
    if (withSlot) setDate(withSlot.date);
    else if (!date || !ds.some((d) => d.date === date)) setDate(ds[0]?.date ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bk.availability]);

  useEffect(() => {
    if (!moved.current) return;
    if (step === 1 && finePointer()) {
      const first = !form.email ? 'email' : !form.firstName ? 'firstName' : null;
      if (first) {
        document.querySelector<HTMLElement>(`[data-field="${first}"] input`)?.focus({ preventScroll: true });
        return;
      }
    }
    headRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const go = (n: number) => {
    moved.current = true;
    setStep(n);
    scrollCardIntoView();
  };

  const submit = async () => {
    const e = validateDetails(form);
    setErrors(e);
    if (Object.keys(e).length) {
      document.querySelector<HTMLElement>(`[data-field="${Object.keys(e)[0]}"] input`)?.focus();
      return;
    }
    if (!slot) {
      go(0);
      return;
    }
    setBanner(null);
    try {
      await bk.book(slot, form);
      moved.current = true;
      requestAnimationFrame(scrollCardIntoView);
    } catch (x) {
      const err = x as ApiError;
      if (err.code === 'VALIDATION' && Object.keys(err.fields || {}).length) {
        setErrors(err.fields as Errors);
        if (Object.keys(err.fields).some((k) => !DETAIL_FIELDS.includes(k))) {
          setSlot(null);
          go(0);
          setBanner('That time can no longer be booked. Please pick another.');
        }
      } else if (err.code === 'SLOT_UNAVAILABLE') {
        setSlot(null);
        go(0);
        setBanner('Someone just took that time. The calendar is refreshed, pick another. Your details are kept.');
      } else {
        setBanner(
          <>
            {err.message || 'Something went wrong.'} {err.code !== 'RATE_LIMITED' && err.code !== 'ALREADY_BOOKED' && 'Try again, or email '}
            <a className="bk-link" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </>,
        );
      }
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 0) {
      if (slot) {
        setBanner(null);
        go(1);
      }
    } else submit();
  };

  const free = isEmail(form.email) && isFreeMail(form.email);
  const when = slot ? `${fmtDay(slot, bk.tz)}, ${fmtTime(slot, bk.tz)}` : '';

  return (
    <div id="booking-card" className="bk scroll-mt-24">
      <div className="bk-card">
        {/* card header */}
        <div className="flex items-center justify-between gap-4 border-b px-6 py-4 sm:px-8" style={{ borderColor: 'var(--bk-line)' }}>
          <div className="flex items-center gap-3">
            <Badge size={36} />
            <div className="leading-tight">
              <div className="text-[14.5px] font-semibold" style={{ color: 'var(--bk-fg)' }}>
                BrandedMark demo
              </div>
              <div className="text-[13px] bk-muted">With the people who build it</div>
            </div>
          </div>
          <div className="text-right leading-tight">
            <div className="text-[14.5px] font-semibold" style={{ color: 'var(--bk-fg)' }}>
              30 min
            </div>
            <div className="text-[13px] bk-muted">Teams call</div>
          </div>
        </div>

        <div className="px-6 pb-6 pt-6 sm:px-8 sm:pb-8">
          {bk.result ? (
            <AutoHeight>
              <Confirmation
                result={bk.result}
                tz={bk.tz}
                product={form.product.trim()}
                cancelled={bk.cancelled}
                onCancel={bk.cancel}
                onRebook={() => {
                  bk.reset();
                  setSlot(null);
                  moved.current = true;
                  setStep(0);
                }}
              />
            </AutoHeight>
          ) : (
            <form noValidate onSubmit={onSubmit}>
              <Progress step={step} labels={STEPS} onJump={go} />
              <Honeypot value={form.website} onChange={(v) => set('website', v)} />

              <AutoHeight className="-mx-2 px-2">
                <div key={step} className={cx(moved.current && 'bk-step-in', 'pt-7')}>
                  {step === 0 && (
                    <div className="bk-pick">
                      <h2 ref={headRef} tabIndex={-1} className="bk-h text-[22px] outline-none">
                        Pick a time that suits you
                      </h2>
                      <TzLine tz={bk.tz} detected={bk.detected} onChange={bk.setTz} className="mb-5 mt-2" />
                      {bk.loading && !bk.availability ? (
                        <PickerSkeleton />
                      ) : bk.error && !bk.availability ? (
                        <Alert>
                          {bk.error}{' '}
                          <button type="button" className="bk-link" onClick={bk.refresh}>
                            Try again
                          </button>
                        </Alert>
                      ) : dates.length === 0 ? (
                        <NoSlots />
                      ) : (
                        <div className={cx('bk-pick-grid', bk.loading && 'opacity-60 transition-opacity')}>
                          <DateGrid from={bk.availability!.from} days={bk.availability!.days} dates={dates} selected={date} onSelect={setDate} tz={bk.tz} />
                          <div>
                            <div className="mb-3 bk-h text-[16px]" aria-live="polite">
                              {day ? day.label : 'Choose a date'}
                            </div>
                            {day && (
                              <TimeGrid
                                key={day.date}
                                slots={day.slots}
                                tz={bk.tz}
                                selected={slot}
                                onSelect={(s) => {
                                  setSlot(s);
                                  setBanner(null);
                                }}
                                label={`Times on ${day.label}`}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                        <h2 ref={headRef} tabIndex={-1} className="bk-h text-[22px] outline-none">
                          {when} <span className="text-[16px] font-medium bk-muted">({tzCity(bk.tz)})</span>
                        </h2>
                        <button type="button" className="bk-link text-[14px]" onClick={() => go(0)}>
                          Change time
                        </button>
                      </div>
                      <p className="mt-1 text-[15px] bk-muted">Where should we send the invite?</p>
                      <div className="mt-6 grid gap-5">
                        <div data-field="email">
                          <TextField
                            label="Work email"
                            type="email"
                            inputMode="email"
                            autoComplete="email"
                            placeholder="you@company.com"
                            value={form.email}
                            onChange={(v) => set('email', v)}
                            onBlur={() => {
                              if (!form.company.trim()) {
                                const c = companyFromEmail(form.email);
                                if (c) set('company', c);
                              }
                            }}
                            error={errors.email}
                            note={
                              free ? (
                                <div className="bk-note">
                                  <IconInfo />
                                  <span>Use your work email if you can, it helps us prepare.</span>
                                </div>
                              ) : null
                            }
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:gap-5">
                          <div data-field="firstName">
                            <TextField label="First name" autoComplete="given-name" value={form.firstName} onChange={(v) => set('firstName', v)} error={errors.firstName} />
                          </div>
                          <div data-field="lastName">
                            <TextField label="Last name" autoComplete="family-name" value={form.lastName} onChange={(v) => set('lastName', v)} error={errors.lastName} />
                          </div>
                        </div>
                        <div data-field="company">
                          <TextField label="Company" autoComplete="organization" value={form.company} onChange={(v) => set('company', v)} error={errors.company} />
                        </div>
                        <TextField
                          label="One product we should look at"
                          optional
                          placeholder="A model name or a link"
                          value={form.product}
                          maxLength={300}
                          onChange={(v) => set('product', v)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </AutoHeight>

              {banner && (
                <div className="mt-5">
                  <Alert>{banner}</Alert>
                </div>
              )}

              {/* footer: one action */}
              <div className={cx('mt-7 flex items-center justify-between gap-3 border-t pt-5', slot && 'bk-foot-sticky')} style={{ borderColor: 'var(--bk-line)' }}>
                {step === 0 ? (
                  <>
                    <span className="text-[14px] bk-muted max-sm:hidden">{slot ? `${fmtDay(slot, bk.tz, true)}, ${fmtTime(slot, bk.tz)}` : 'Choose a day, then a time.'}</span>
                    <button type="submit" className="bk-btn min-w-0 max-sm:flex-1 sm:min-w-[180px]" aria-disabled={!slot}>
                      {slot ? (
                        <>
                          Continue <IconArrow />
                        </>
                      ) : (
                        'Choose a time'
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="bk-ghost" onClick={() => go(0)} aria-label="Back to times">
                      <IconArrow back /> <span className="max-sm:sr-only">Back</span>
                    </button>
                    <button type="submit" className="bk-btn go min-w-0 max-sm:flex-1 sm:min-w-[220px]" aria-disabled={bk.submitting}>
                      {bk.submitting ? (
                        <>
                          <Spinner /> Booking
                        </>
                      ) : (
                        <span className="whitespace-nowrap">
                          Book <span className="max-[420px]:hidden">{slot ? fmtDay(slot, bk.tz) + ', ' : ''}</span>
                          {slot ? fmtTime(slot, bk.tz) : ''}
                        </span>
                      )}
                    </button>
                  </>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
      <p className="mt-4 px-2 text-center text-[13px] text-bm-muted">
        We use your details only to arrange and prepare this call.{' '}
        <SmartLink href="/legal/privacy/" className="underline underline-offset-2 hover:text-bm-indigo">
          Privacy notice
        </SmartLink>
      </p>
    </div>
  );
}

const TRUST: { t: string; d: string; href?: string; l?: string }[] = [
  {
    t: 'Works with your ERP',
    d: 'Epicor Kinetic connects natively; other ERPs through our API.',
  },
  {
    t: 'Start with one product line',
    d: 'Learn who your owners are, then grow.',
  },
  { t: 'GDPR', d: 'A data processing agreement is in the terms.' },
];

export default function BookDemo() {
  usePageMeta('Book 30 minutes', booking.lede);
  const src = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('src') || undefined : undefined;
  const [booked, setBooked] = useState(false);
  const title = booked ? booking.booked.title : booking.title;

  return (
    <div className="relative bg-bm-alt">
      <div className="wrap grid gap-x-16 gap-y-10 pb-24 pt-10 sm:pt-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,568px)] lg:pb-32 lg:pt-16 xl:gap-x-20">
        {/* promise */}
        <header className="lg:col-start-1 lg:row-start-1">
          <h1
            className="font-extrabold text-bm-indigo"
            style={{
              fontSize: 'clamp(38px, 3.7vw, 54px)',
              lineHeight: 1.02,
              letterSpacing: '-0.04em',
            }}
          >
            {title[0]}
            <br />
            <Swipe draw key={title[1]}>
              {title[1]}
            </Swipe>
          </h1>
          <p className="t-lede mt-5 max-w-[30rem] max-sm:text-[17px] sm:mt-6">{booked ? booking.booked.lede : booking.lede}</p>
        </header>

        {/* the card */}
        <div className="lg:sticky lg:top-24 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-start">
          <BookingCard source={src} onBooked={setBooked} />
        </div>

        {/* expectations, people, trust, one quiet alternative */}
        <div className="lg:col-start-1 lg:row-start-2">
          {booked ? (
            <section aria-labelledby="bk-see">
              <h2 id="bk-see" className="text-[14.5px] font-semibold text-bm-muted">
                What you'll see
              </h2>
              <ol className="mt-4 border-t border-bm-line">
                {booking.booked.see.map((x, i) => (
                  <li key={x.t} className="flex items-baseline gap-4 border-b border-bm-line py-4">
                    <span className="font-mono text-[12.5px] text-bm-emerald">0{i + 1}</span>
                    <span>
                      <span className="block text-[17px] font-medium text-bm-indigo">{x.t}</span>
                      <span className="mt-0.5 block text-[15px] leading-snug text-bm-muted">{x.d}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ) : (
            <section aria-labelledby="bk-expect">
              <h2 id="bk-expect" className="text-[14.5px] font-semibold text-bm-muted">
                In the 30 minutes
              </h2>
              <ol className="mt-4 border-t border-bm-line">
                {booking.expect.map((x, i) => (
                  <li key={x} className="flex items-baseline gap-4 border-b border-bm-line py-4">
                    <span className="font-mono text-[12.5px] text-bm-emerald">0{i + 1}</span>
                    <span className="text-[17px] font-medium text-bm-indigo">{x}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          <section aria-labelledby="bk-who" className="mt-12">
            <h2 id="bk-who" className="text-[20px] font-bold leading-snug tracking-[-0.02em] text-bm-indigo">
              {founders.title[0]} <span className="text-bm-emerald">{founders.title[1]}</span>
            </h2>
            <dl className="mt-5 grid gap-4 sm:grid-cols-3">
              {founders.credentials.map((c) => (
                <div key={c.k} className="border-t-2 border-bm-indigo pt-3">
                  <dt className="text-[14.5px] font-semibold text-bm-emerald">{c.k}</dt>
                  <dd className="mt-1.5 text-[14.5px] leading-snug text-bm-ink">{c.v}</dd>
                </div>
              ))}
            </dl>
          </section>

          {!booked && (
            <ul className="mt-12 border-t border-bm-line">
              {TRUST.map((x) => (
                <li key={x.t} className="grid gap-1 border-b border-bm-line py-3.5 sm:grid-cols-[220px_1fr] sm:gap-4">
                  <div className="flex items-center gap-2 text-[15px] font-semibold text-bm-indigo">
                    <span className="text-bm-emerald">
                      <IconCheck size={16} />
                    </span>
                    {x.t}
                  </div>
                  <p className="text-[14.5px] leading-snug text-bm-muted max-sm:pl-6 sm:pt-px">
                    {x.d}{' '}
                    {x.href && (
                      <SmartLink href={x.href} className="font-medium text-bm-indigo underline decoration-bm-line underline-offset-2 hover:decoration-bm-indigo">
                        {x.l}
                      </SmartLink>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-8 text-[15px] text-bm-muted">
            {booked ? 'While you wait,' : 'Rather look first?'}{' '}
            <SmartLink
              href={cta.secondaryHref}
              className="inline-flex items-center gap-1.5 font-semibold text-bm-indigo underline decoration-bm-line underline-offset-4 hover:decoration-bm-indigo"
            >
              {booked ? 'take the 3-minute tour' : 'Take the 3-minute tour'} <IconArrow />
            </SmartLink>
          </p>
        </div>
      </div>
    </div>
  );
}
